import { useEffect, useRef, useState } from 'react';
import { Check, Copy, X } from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '@/lib/utils';
import { startTitleFlash, stopTitleFlash } from '@/lib/title-flash';
import { playOtpAlert } from '@/lib/sound';

export interface OtpEvent {
  otpLogId: string;
  otpCode: string;
  smsBody: string;
  senderId: string;
  action: string;
  cardId?: string;
  cardRequestId?: string;
  bankAccountId?: string;
  bankTransferRequestId?: string;
  sessionId?: string;
  receivedAt?: string;
  /** Optional UI-side enrichments — set by the dashboard hook */
  cardNickname?: string;
  vendor?: string;
  clientName?: string;
  /** Set when the SMS-side merchant differs from the user-declared vendor.
   *  Surfaced as a warning so the user verifies before entering the OTP. */
  vendorMismatchActual?: string | null;
  /** Twin cross-delivery note (set when same vendor + 2-min window = OTP also
   *  delivered to a colleague's session). */
  twinNote?: string | null;
}

interface Props {
  event: OtpEvent;
  onDismiss: () => void;
  /**
   * Admins see the full routing path label (SESSION / APPROVAL / GRACE_FORWARD).
   * Ops/accounts see a generic "Approved request" — knowing they're in a
   * silent session-mode trust window would let them push through requests
   * that would normally need explicit admin approval.
   */
  showRoutingPath?: boolean;
}

const DURATION_SEC = 90;

export function OtpOverlay(props: Props) {
  const { event, onDismiss } = props;
  const [secondsLeft, setSecondsLeft] = useState(DURATION_SEC);
  const [copied, setCopied] = useState(false);
  const [paused, setPaused] = useState(false);
  // Pause-aware countdown: track total elapsed time excluding pause windows so
  // the user can hover/read the SMS body without the overlay timing out.
  const elapsedRef = useRef(0);
  const lastTickRef = useRef(Date.now());

  // On mount: copy + sound + title flash + countdown
  useEffect(() => {
    if (event.otpCode) {
      void navigator.clipboard
        .writeText(event.otpCode)
        .then(() => setCopied(true))
        .catch(() => setCopied(false));
      startTitleFlash(event.otpCode);
      playOtpAlert();
    }
    elapsedRef.current = 0;
    lastTickRef.current = Date.now();
    const tick = window.setInterval(() => {
      const now = Date.now();
      if (!paused) {
        elapsedRef.current += (now - lastTickRef.current) / 1000;
      }
      lastTickRef.current = now;
      const remain = Math.max(0, DURATION_SEC - Math.floor(elapsedRef.current));
      setSecondsLeft(remain);
      if (remain === 0) window.clearInterval(tick);
    }, 1000);
    return () => {
      window.clearInterval(tick);
      stopTitleFlash();
    };
  }, [event.otpCode, paused]);

  const phase: 'safe' | 'warn' | 'crit' = secondsLeft > 30 ? 'safe' : secondsLeft > 10 ? 'warn' : 'crit';
  const phaseColor =
    phase === 'safe'
      ? 'stroke-success text-success'
      : phase === 'warn'
        ? 'stroke-warning text-warning'
        : 'stroke-destructive text-destructive';

  const ringFraction = secondsLeft / DURATION_SEC;
  const RADIUS = 88;
  const CIRCUM = 2 * Math.PI * RADIUS;

  const copyAgain = () => {
    if (!event.otpCode) return;
    void navigator.clipboard.writeText(event.otpCode).then(() => setCopied(true));
  };

  const grouped = event.otpCode ? event.otpCode.replace(/(.{3})/g, '$1 ').trim() : '';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="otp-overlay-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 px-4 backdrop-blur-sm animate-overlay-in"
      onClick={(e) => {
        if (e.target === e.currentTarget) onDismiss();
      }}
    >
      <div
        className="relative w-full max-w-xl rounded-2xl border bg-card p-8 shadow-2xl animate-content-in"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
      >
        {/* dismiss */}
        <button
          onClick={onDismiss}
          className="absolute right-4 top-4 rounded-md p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>

        {/* header */}
        <div className="flex items-center gap-2 text-sm font-medium uppercase tracking-wider text-muted-foreground">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-60" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
          </span>
          OTP Ready
        </div>
        <h2 id="otp-overlay-title" className="mt-2 text-xs text-muted-foreground">
          {actionLabel(event.action, props.showRoutingPath ?? false)}
          {event.senderId ? ` · ${event.senderId}` : ''}
        </h2>

        {/* ring + code */}
        <div className="mt-6 flex flex-col items-center gap-4">
          <div className="relative flex h-52 w-52 items-center justify-center">
            <svg className="absolute inset-0 -rotate-90" viewBox="0 0 200 200">
              <circle
                cx="100"
                cy="100"
                r={RADIUS}
                strokeWidth="8"
                fill="none"
                className="stroke-muted"
              />
              <circle
                cx="100"
                cy="100"
                r={RADIUS}
                strokeWidth="8"
                strokeLinecap="round"
                fill="none"
                strokeDasharray={CIRCUM}
                strokeDashoffset={CIRCUM * (1 - ringFraction)}
                className={cn('transition-all duration-200', phaseColor)}
              />
            </svg>
            <div className="text-center">
              <div className={cn('font-mono text-3xl font-semibold tabular-nums', phaseColor)}>
                {secondsLeft}s
              </div>
              <div className="mt-1 text-[10px] uppercase tracking-widest text-muted-foreground">
                {paused ? 'paused' : phase === 'crit' ? 'expiring' : phase === 'warn' ? 'soon' : 'valid'}
              </div>
            </div>
          </div>

          {/* the code itself */}
          {event.otpCode ? (
            <button
              onClick={copyAgain}
              className="group relative rounded-xl bg-secondary px-6 py-4 transition-colors hover:bg-secondary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <div className="font-mono text-6xl font-bold tabular-nums tracking-[0.18em] text-foreground">
                {grouped}
              </div>
              <div className="mt-3 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
                {copied ? (
                  <>
                    <Check className="h-3.5 w-3.5 text-success" />
                    <span>Copied — paste with Ctrl+V</span>
                  </>
                ) : (
                  <>
                    <Copy className="h-3.5 w-3.5" />
                    <span>Click to copy</span>
                  </>
                )}
              </div>
            </button>
          ) : (
            <div className="rounded-xl bg-secondary px-6 py-4 text-sm text-muted-foreground">
              No OTP code in body — review SMS below
            </div>
          )}

          {/* context */}
          <div className="text-center text-sm text-muted-foreground">
            {[event.cardNickname, event.vendor, event.clientName].filter(Boolean).join(' · ') || '—'}
          </div>

          {event.vendorMismatchActual ? (
            <div className="w-full rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning">
              <span className="font-semibold">Vendor differs:</span> you raised this for{' '}
              <span className="font-semibold">{event.vendor ?? 'your vendor'}</span>, OTP arrived from{' '}
              <span className="font-semibold">{event.vendorMismatchActual}</span>. Verify it's the right
              merchant before entering.
            </div>
          ) : null}

          {event.twinNote ? (
            <div className="w-full rounded-md border border-primary/40 bg-primary/10 px-3 py-2 text-xs text-primary">
              {event.twinNote}
            </div>
          ) : null}

          {/* Staleness indicator — a back-routed / recovered OTP can surface
              minutes after it arrived. Bank OTPs typically expire in ~5 min,
              so warn before the operator types a dead code. */}
          {(() => {
            if (!event.receivedAt) return null;
            const ageMin = Math.floor((Date.now() - new Date(event.receivedAt).getTime()) / 60000);
            if (ageMin < 3) return null;
            const expired = ageMin >= 7;
            return (
              <div
                className={cn(
                  'w-full rounded-md border px-3 py-2 text-xs',
                  expired
                    ? 'border-destructive/40 bg-destructive/10 text-destructive'
                    : 'border-warning/40 bg-warning/10 text-warning',
                )}
              >
                {expired ? <span className="font-semibold">Likely expired — </span> : null}
                This OTP arrived {ageMin} min ago.{' '}
                {expired
                  ? 'Bank OTPs usually expire within ~5 min. If it fails, trigger a fresh one.'
                  : 'Use it quickly.'}
              </div>
            );
          })()}
        </div>

        {/* full SMS body */}
        <div className="mt-6 rounded-md border bg-muted/30 px-3 py-2.5 font-mono text-xs leading-relaxed text-muted-foreground">
          {event.smsBody}
        </div>

        {/* actions */}
        <div className="mt-6 flex justify-end">
          <Button onClick={onDismiss} variant="outline">
            Dismiss
          </Button>
        </div>
      </div>
    </div>
  );
}

function actionLabel(action: string, showRoutingPath: boolean): string {
  if (!showRoutingPath) {
    // Non-admin view: collapse all OTP-delivery routes into one generic label.
    return 'Approved request';
  }
  switch (action) {
    case 'APPROVAL':
      return 'Approved request';
    case 'SESSION':
      return 'Active session';
    case 'GRACE_FORWARD':
      return 'Grace window resend';
    case 'AUTO_FORWARD':
      return 'Auto-forwarded';
    default:
      return action.replace(/_/g, ' ').toLowerCase();
  }
}
